import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Link,
  CircularProgress,
  Alert,
  Snackbar,
  Box,
  Divider,
} from '@mui/material';
// components
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import Image from '../components/Image';
// sections
import { UserListHead } from '../sections/@dashboard/user';
import config from '../config';
import { auctionService } from '../services/auction.service';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'index', label: 'No', alignRight: false },
  { id: 'item', label: 'Item', alignRight: false },
  { id: 'title', label: 'Title', alignRight: false },
  { id: 'yahooAccount', label: 'YH Account', alignRight: false },
  { id: 'auctionDate', label: 'Auction Date', alignRight: false },
  { id: 'endDate', label: 'End Date', alignRight: false },
  { id: 'currentPrice', label: 'Current Price', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '', label: '', alignRight: true },
];

const STATUS_OPTIONS = {
  watching: { label: 'Đang theo dõi', color: 'info' },
  active: { label: 'Đang đấu', color: 'warning' },
  ended: { label: 'Kết thúc', color: 'default' },
  won: { label: 'Thắng', color: 'success' },
  lost: { label: 'Thua', color: 'error' },
};

const emptyForm = {
  productUrl: '',
  yahooAccount: '',
  auctionStep: '',
  maxPrice: '',
  title: '',
  imageUrl: '',
  auctionDate: '',
  endDate: '',
  currentPrice: '',
};

// ----------------------------------------------------------------------

function formatDate(value) {
  if (!value) return '-';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : value;
}

function toDateTimeLocalValue(value) {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DDTHH:mm') : '';
}

function formatPrice(value) {
  if (value === null || value === undefined || value === '') return '-';
  return `¥${Number(value).toLocaleString('en-US')}`;
}

function getStatusMeta(status) {
  if (!status) return STATUS_OPTIONS.watching;
  const key = String(status).toLowerCase();
  return STATUS_OPTIONS[key] || { label: status, color: 'default' };
}

function extractAuctionId(productUrl) {
  if (!productUrl) return '-';
  const match = String(productUrl).match(/\/auction\/([^/?#]+)/i);
  return match ? match[1] : productUrl;
}

function getApiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.length && data.length < 300 && !data.includes('<')) {
    return data;
  }
  if (data?.title && typeof data.title === 'string') return data.title;
  if (data?.message && typeof data.message === 'string') return data.message;
  if (error?.message && typeof error.message === 'string') return error.message;
  return fallback;
}

// ----------------------------------------------------------------------

export default function AuctionPage() {
  const API_HOST = config.API_URL;

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [formError, setFormError] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const previewTimerRef = useRef(null);
  const previewRequestIdRef = useRef(0);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const loadItems = useCallback(() => {
    setLoading(true);
    auctionService
      .search({
        pageIndex: page,
        pageSize: rowsPerPage,
      })
      .then((res) => {
        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotalCount(res?.totalCount ?? 0);
      })
      .catch((error) => {
        setItems([]);
        setTotalCount(0);
        showToast(getApiErrorMessage(error, 'Không tải được danh sách đấu giá'), 'error');
      })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]);

  const loadAccounts = () => {
    axios
      .get(`${API_HOST}/api/YahooAccount`)
      .then((res) => {
        if (Array.isArray(res.data)) {
          setAccounts(res.data);
        }
      })
      .catch(() => {
        setAccounts([]);
      });
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(
    () => () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    },
    []
  );

  const applyPreview = (preview) => {
    setForm((prev) => ({
      ...prev,
      title: preview?.title || '',
      imageUrl: preview?.imageUrl || '',
      auctionDate: toDateTimeLocalValue(preview?.auctionDate),
      endDate: toDateTimeLocalValue(preview?.endDate),
      currentPrice:
        preview?.currentPrice === null || preview?.currentPrice === undefined
          ? ''
          : String(preview.currentPrice),
    }));
  };

  const crawlAuctionDetail = (productUrl, yahooAccount) => {
    const url = (productUrl || '').trim();
    if (!url || !/^https?:\/\//i.test(url)) {
      return;
    }

    const requestId = ++previewRequestIdRef.current;
    setFetchingPreview(true);
    setFormError('');

    auctionService
      .preview({ productUrl: url, yahooAccount })
      .then((preview) => {
        if (requestId !== previewRequestIdRef.current) return;
        applyPreview(preview);
      })
      .catch((error) => {
        if (requestId !== previewRequestIdRef.current) return;
        setForm((prev) => ({
          ...prev,
          title: '',
          imageUrl: '',
          auctionDate: '',
          endDate: '',
          currentPrice: '',
        }));
        setFormError(getApiErrorMessage(error, 'Không lấy được thông tin đấu giá từ Yahoo'));
      })
      .finally(() => {
        if (requestId === previewRequestIdRef.current) {
          setFetchingPreview(false);
        }
      });
  };

  const schedulePreview = (productUrl, yahooAccount) => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }
    previewTimerRef.current = setTimeout(() => {
      crawlAuctionDetail(productUrl, yahooAccount);
    }, 600);
  };

  const handleOpenDialog = () => {
    setForm(emptyForm);
    setFormError('');
    setFetchingPreview(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }
    previewRequestIdRef.current += 1;
    setOpenDialog(false);
    setForm(emptyForm);
    setFormError('');
    setFetchingPreview(false);
  };

  const handleFormChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      return next;
    });

    if (field === 'productUrl') {
      setFormError('');
      if (!value?.trim()) {
        setForm((prev) => ({
          ...prev,
          productUrl: value,
          title: '',
          imageUrl: '',
          auctionDate: '',
          endDate: '',
          currentPrice: '',
        }));
        return;
      }
      schedulePreview(value, form.yahooAccount);
    }

    if (field === 'yahooAccount' && form.productUrl?.trim()) {
      schedulePreview(form.productUrl, value);
    }
  };

  const handleUrlBlur = () => {
    if (form.productUrl?.trim()) {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
      crawlAuctionDetail(form.productUrl, form.yahooAccount);
    }
  };

  const handleAddItem = () => {
    if (!form.productUrl?.trim()) {
      setFormError('Vui lòng nhập Link Sản Phẩm');
      return;
    }
    if (!form.yahooAccount?.trim()) {
      setFormError('Vui lòng chọn Tài Khoản Đấu Giá');
      return;
    }
    if (form.auctionStep === '' || Number(form.auctionStep) <= 0) {
      setFormError('Vui lòng nhập Bước Giá hợp lệ');
      return;
    }
    if (form.maxPrice === '' || Number(form.maxPrice) <= 0) {
      setFormError('Vui lòng nhập Giá Tối Đa hợp lệ');
      return;
    }

    setSaving(true);
    setFormError('');

    const payload = {
      productUrl: form.productUrl.trim(),
      title: form.title?.trim() || null,
      imageUrl: form.imageUrl?.trim() || null,
      yahooAccount: form.yahooAccount.trim(),
      auctionStep: Number(form.auctionStep),
      maxPrice: Number(form.maxPrice),
      status: 'watching',
      currentPrice: form.currentPrice === '' ? null : Number(form.currentPrice),
      auctionDate: form.auctionDate ? dayjs(form.auctionDate).toISOString() : null,
      endDate: form.endDate ? dayjs(form.endDate).toISOString() : null,
    };

    auctionService
      .create(payload)
      .then(() => {
        handleCloseDialog();
        showToast('Đã thêm đấu giá để theo dõi');
        if (page === 0) {
          loadItems();
        } else {
          setPage(0);
        }
      })
      .catch((error) => {
        setFormError(getApiErrorMessage(error, 'Không thể thêm đấu giá'));
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (id) => {
    if (!id) return;

    auctionService
      .remove(id)
      .then(() => {
        showToast('Đã xóa đấu giá');
        loadItems();
      })
      .catch((error) => {
        showToast(getApiErrorMessage(error, 'Không thể xóa đấu giá'), 'error');
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const isEmpty = !loading && items.length === 0;
  const hasPreview =
    form.title || form.imageUrl || form.auctionDate || form.endDate || form.currentPrice !== '';

  return (
    <>
      <Helmet>
        <title> Đấu Giá | DT Cargo </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4">Đấu Giá</Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleOpenDialog}
          >
            Thêm Đấu Giá
          </Button>
        </Stack>

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1100 }}>
              <Table>
                <UserListHead
                  order="asc"
                  orderBy="index"
                  headLabel={TABLE_HEAD}
                  rowCount={items.length}
                  numSelected={0}
                  onRequestSort={() => {}}
                  onSelectAllClick={() => {}}
                />
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  )}

                  {!loading &&
                    items.map((item, index) => {
                      const statusMeta = getStatusMeta(item.status);
                      const no = page * rowsPerPage + index + 1;
                      const title = item.title || extractAuctionId(item.productUrl);

                      return (
                        <TableRow hover key={item.id || no} tabIndex={-1}>
                          <TableCell align="left">
                            <Typography variant="subtitle2">{no}</Typography>
                          </TableCell>

                          <TableCell align="left">
                            <Link
                              href={item.productUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ display: 'inline-flex', alignItems: 'center' }}
                            >
                              <Image
                                disabledEffect
                                alt={title}
                                src={item.imageUrl || '/assets/placeholder.svg'}
                                sx={{ borderRadius: 1.5, width: 56, height: 56 }}
                              />
                            </Link>
                          </TableCell>

                          <TableCell align="left" sx={{ maxWidth: 280 }}>
                            <Link
                              href={item.productUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="inherit"
                              underline="hover"
                            >
                              <Typography variant="subtitle2" noWrap title={title}>
                                {title}
                              </Typography>
                            </Link>
                          </TableCell>

                          <TableCell align="left">
                            <Typography variant="body2" noWrap>
                              {item.yahooAccount || '-'}
                            </Typography>
                          </TableCell>

                          <TableCell align="left">{formatDate(item.auctionDate)}</TableCell>
                          <TableCell align="left">{formatDate(item.endDate)}</TableCell>
                          <TableCell align="left">{formatPrice(item.currentPrice)}</TableCell>
                          <TableCell align="left">
                            <Label color={statusMeta.color}>{statusMeta.label}</Label>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                              <Iconify icon="eva:trash-2-outline" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                  {isEmpty && (
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 6 }}>
                        <Paper sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" paragraph>
                            Chưa có đấu giá nào
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Nhấn &quot;Thêm Đấu Giá&quot; để theo dõi sản phẩm đấu giá.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Thêm Đấu Giá để theo dõi</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Link Sản Phẩm"
              value={form.productUrl}
              onChange={handleFormChange('productUrl')}
              onBlur={handleUrlBlur}
              fullWidth
              required
              placeholder="https://page.auctions.yahoo.co.jp/jp/auction/..."
              InputProps={{
                endAdornment: fetchingPreview ? <CircularProgress size={20} /> : null,
              }}
            />
            <TextField
              select
              label="Tài Khoản Đấu Giá"
              value={form.yahooAccount}
              onChange={handleFormChange('yahooAccount')}
              fullWidth
              required
            >
              {accounts.length === 0 ? (
                <MenuItem value="" disabled>
                  Không có tài khoản
                </MenuItem>
              ) : (
                accounts.map((acc) => (
                  <MenuItem key={acc.id || acc.accountName} value={acc.accountName}>
                    {acc.accountName}
                  </MenuItem>
                ))
              )}
            </TextField>
            <TextField
              label="Bước Giá"
              type="number"
              value={form.auctionStep}
              onChange={handleFormChange('auctionStep')}
              fullWidth
              required
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Giá Tối Đa"
              type="number"
              value={form.maxPrice}
              onChange={handleFormChange('maxPrice')}
              fullWidth
              required
              inputProps={{ min: 1 }}
            />

            {(fetchingPreview || hasPreview) && (
              <>
                <Divider />
                <Typography variant="subtitle2">Thông tin từ Yahoo Auction</Typography>

                {form.imageUrl ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Image
                      disabledEffect
                      alt={form.title || 'Auction preview'}
                      src={form.imageUrl}
                      sx={{ borderRadius: 1.5, width: 120, height: 120 }}
                    />
                  </Box>
                ) : null}

                <TextField label="Title" value={form.title} fullWidth InputProps={{ readOnly: true }} />
                <TextField
                  label="Thumbnail URL"
                  value={form.imageUrl}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Auction Start Date"
                    type="datetime-local"
                    value={form.auctionDate}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="End Date"
                    type="datetime-local"
                    value={form.endDate}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
                <TextField
                  label="Auction Price (JPY)"
                  value={form.currentPrice}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </>
            )}

            {formError ? (
              <Alert severity="error">{typeof formError === 'string' ? formError : 'Có lỗi xảy ra'}</Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Hủy
          </Button>
          <Button
            variant="contained"
            onClick={handleAddItem}
            disabled={saving || fetchingPreview}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Iconify icon="eva:plus-fill" />}
          >
            Thêm theo dõi
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
